import { useCallback, useEffect, useRef, useState, type CSSProperties, type ReactNode } from 'react'
import { createPortal } from 'react-dom'
import { useCanGoBack, useNavigate, useSearch } from '@tanstack/react-router'
import { useQuery, useQueryClient } from '@tanstack/react-query'
import type {
  DocRef,
  FullAnalysisPayload,
  Leave,
  LeaveDetailPayload,
  LeaveDetailTimelineEntry,
  LeaveRuleResult,
  LeaveTypeId,
} from '@lms/shared-types'
import {
  getLeaveDetail,
  approveLeave,
  capacityAckRequired,
  type CapacityAckSnapshot,
  rejectLeave,
  cancelLeave,
  requestLeaveCancellation,
  approveCancellation,
  rejectCancellation,
  cancelHrLeave,
} from '../api/leaves'
import { getDocumentUrl } from '../api/documents'
import { previewExistingLeave } from '../api/leaves'
import { LeaveCheck } from '../components/employee/LeaveCheck'
import { LeaveDayBreakdownFromDetail } from '../components/employee/LeaveDayBreakdown'
import { LEAVE_TYPE_HALF_DAY_ALLOWED } from '../lib/leave-type-meta'
import { buildLeaveCheckItems, type LeaveDetailFacts } from './leave-detail-check'
import { DocumentAttachmentRow } from '../components/employee/DocumentAttachmentRow'
import { LeaveDocumentUpload } from '../components/employee/LeaveDocumentUpload'
import { Card } from '../components/primitives/Card'
import { Collapsible } from '../components/primitives/Collapsible'
import { Tooltip } from '../components/primitives/Tooltip'
import { ConfirmationDialog } from '../components/primitives/ConfirmationDialog'
import { Modal } from '../components/primitives/Modal'
import { Skeleton } from '../components/primitives/Skeleton'
import { LEAVE_TYPE_STYLE } from '../lib/leave-type-palette'
import { Zone4RequesterView } from '../components/manager/Zone4RequesterView'
import { buildDecisionSentence } from '../components/manager/decision-sentence'
import { flags } from '../lib/feature-flags'
import {
  CANCEL_REQUEST_REASON_MIN,
  canRequestCancellation,
} from '../lib/leave-cancellation'
import { CompositeRequestBlock } from '../components/manager/CompositeRequestBlock'
import { BlackoutResetBanner } from '../components/employee/BlackoutResetBanner'
import { EndorseToDirector } from '../components/manager/EndorseToDirector'
import { HrReviewActions } from '../components/hr/HrReviewActions'
import { LeaveTeamCalendar } from '../components/manager/LeaveTeamCalendar'
import { Icon, type IconName } from '../components/primitives/Icon'
import { IlmuMark } from '../components/primitives/IlmuMark'
import { useToast } from '../components/primitives/Toast'
import { useSession } from '../auth/auth-context'
import { displayName } from '../lib/display-name'
import { fmtDateLong } from '../lib/format'
import { FadeIn } from '../components/primitives/FadeIn'
import { ApproverPath } from '../components/leave/ApproverPath'
import { Avatar } from '../components/primitives/Avatar'
import { RhythmAvatar } from '../components/manager-team/RhythmAvatar'
import { useViewport } from '../lib/use-viewport'
import { RHYTHM_STYLE } from '../lib/rhythm-style'
import { useReducedMotion } from '../lib/use-reduced-motion'

type State =
  | { status: 'loading' }
  | { status: 'error'; message: string }
  | { status: 'ok'; data: LeaveDetailPayload }

const STATUS_PALETTE: Record<string, { fg: string; bg: string; label: string }> = {
  pending: { fg: 'var(--text-2)', bg: 'var(--surface-2)', label: 'Pending' },
  // Shown to HR / approvers only — the owner's own view masks this to "Pending"
  // (see displayStatusKey), so the employee never learns HR is reviewing.
  pending_hr_review: {
    fg: 'var(--warning-fg)',
    bg: 'var(--warning-bg)',
    label: 'Pending HR review',
  },
  approved: { fg: 'var(--success-fg)', bg: 'var(--success-bg)', label: 'Approved' },
  rejected: { fg: 'var(--danger-fg)', bg: 'var(--danger-bg)', label: 'Rejected' },
  escalated: { fg: 'var(--warning-fg)', bg: 'var(--warning-bg)', label: 'Escalated' },
  cancelled: { fg: 'var(--text-3)', bg: 'var(--surface-2)', label: 'Cancelled' },
  pending_cancellation: {
    fg: 'var(--warning-fg)',
    bg: 'var(--warning-bg)',
    label: 'Cancellation requested',
  },
  needs_review: {
    fg: 'var(--warning-fg)',
    bg: 'var(--warning-bg)',
    // RFC 004 §4.2 — surfaced when a previously-approved annual leave
    // now sits inside a freshly-declared blackout. The owner-facing copy
    // ("Blackout declared — your call") is non-technical and points at
    // the resubmit panel below the header. Approver-side surfaces use
    // their own labels; this palette key is the page-wide default.
    label: 'Blackout declared — your call',
  },
}

/** Shared style for the header chips (Emergency / status / sick-blackout) so
 *  they read as one consistent set — same size, weight, casing; only colour
 *  differs. Colours are applied at each call site. */
const HEADER_CHIP_STYLE: CSSProperties = {
  display: 'inline-flex',
  alignItems: 'center',
  fontSize: 11,
  fontWeight: 700,
  letterSpacing: '0.06em',
  textTransform: 'uppercase',
  padding: '3px 10px',
  borderRadius: 999,
}

/** Leave Rhythm label + icon by wellbeing band. Ring/fill colours come from
 *  the shared RHYTHM_STYLE palette (same shades as the home-page health card). */
const RHYTHM_BAND: Record<
  'low' | 'medium' | 'high',
  { label: string; short: string; icon: IconName }
> = {
  low: { label: 'Leave rhythm looks healthy', short: 'Steady', icon: 'check_circle' },
  medium: { label: 'Leave rhythm worth a look', short: 'Worth a look', icon: 'warning' },
  high: { label: 'Leave rhythm needs attention', short: 'Needs attention', icon: 'error' },
}

const ACTION_LABEL: Record<string, string> = {
  'leave.created': 'Leave submitted',
  'leave.created.from_conversation': 'Leave submitted (via Slack)',
  'leave.approved': 'Approved',
  'leave.rejected': 'Rejected',
  'leave.escalated': 'Escalated',
  'leave.hr_cancelled': 'Cancelled by HR',
  'leave.cancelled': 'Cancelled by requester',
  // Approved-leave cancellation lifecycle (request → manager decision).
  'leave.cancellation_requested': 'Cancellation requested',
  'leave.cancellation_approved': 'Cancellation approved',
  'leave.cancellation_rejected': 'Cancellation rejected',
  'leave.note_added': 'Note added',
  // Overflow composite policy (Slice 4a) — the recommendation step needs
  // a human label in the timeline; without it the audit row appears as
  // the raw action string.
  'leave.manager_recommended': 'Manager recommended to Director',
  // HR out-of-system encode — the leave was approved offline and encoded by HR
  // on the employee's behalf. The approval justification renders as the note.
  'leave.out_of_system_encoded': 'Encoded by HR (out of system)',
  // HR-first review stage (HR_DOCUMENT_REVIEW).
  'hr_review.forwarded': 'Forwarded to approver by HR',
  'hr_review.rejected': 'Rejected by HR',
  'hr_review.document_requested': 'Document requested by HR',
  'leave.document.uploaded': 'Document uploaded',
}

const APPROVER_ROLES = new Set(['manager', 'hod', 'hr', 'director', 'exec_director'])
// Roles that may HR-override-cancel an approved leave (matches the api guard
// role_at_least(HR): HR and above). Min reason length mirrors the endpoint.
const HR_CANCEL_ROLES = new Set(['hr', 'director', 'exec_director', 'superadmin'])
const HR_CANCEL_REASON_MIN = 10
// Optional approval note: when supplied it is sent as the approve `reason`.
// Any length is accepted (empty is sent as undefined); the API caps it at
// 500 chars (LeaveApproveRequest), mirrored here by the textarea maxLength.
const APPROVAL_NOTE_MAX = 500

function formatCompositeDays(n: number): string {
  return Number.isInteger(n) ? String(n) : n.toFixed(1)
}

/**
 * /leave?id=<uuid> — single leave detail view.
 *
 * One source of truth for the manager queue's row click, the employee's
 * post-submit redirect, and the Slack "Open in app ↗" deep link.
 */
export function LeaveDetailPage(): JSX.Element {
  const navigate = useNavigate()
  const canGoBack = useCanGoBack()
  const session = useSession()
  const { isDesktop } = useViewport()
  const search = useSearch({ strict: false }) as Record<string, string | undefined>
  const leaveId = search['id'] ?? null

  const queryClient = useQueryClient()
  const [state, setState] = useState<State>({ status: 'loading' })
  const [busy, setBusy] = useState(false)
  const [cancelOpen, setCancelOpen] = useState(false)
  const [cancelReason, setCancelReason] = useState('')
  // true when the open cancel dialog is the HR manual override (not the
  // owner's self-cancel / request flow).
  const [hrCancelMode, setHrCancelMode] = useState(false)
  const { notify } = useToast()
  const [rejectOpen, setRejectOpen] = useState(false)
  const [rejectReason, setRejectReason] = useState<string>('')
  const [rejectError, setRejectError] = useState<string | null>(null)
  // Over-capacity approve confirm (capacity de-gated 2026-09-24): approving a
  // leave that puts the team over its concurrent-absence cap is allowed, but the
  // manager confirms it first. The snapshot comes from the detail payload
  // (proactive) or the /approve 409 (race-safe fallback).
  const [capacityConfirmOpen, setCapacityConfirmOpen] = useState(false)
  const [capacitySnapshot, setCapacitySnapshot] =
    useState<CapacityAckSnapshot | null>(null)
  const [capacityConfirmError, setCapacityConfirmError] = useState<string | null>(
    null,
  )
  const [previewDoc, setPreviewDoc] = useState<DocRef | null>(null)
  // Director cascade decision modal state (composite approve/reject).
  const [directorDecisionMode, setDirectorDecisionMode] = useState<
    'approve' | 'reject' | null
  >(null)
  const [directorDecisionNote, setDirectorDecisionNote] = useState<string>('')
  const [directorDecisionError, setDirectorDecisionError] = useState<
    string | null
  >(null)
  const [directorDecisionPending, setDirectorDecisionPending] =
    useState<boolean>(false)
  // Optional free-text note the approver records with an approval. When
  // non-empty it is sent as the approve `reason`, which the API writes to the
  // `leave.approved` audit row and the detail timeline surfaces verbatim under
  // Activity — so what the approver types is exactly what gets recorded.
  const [approvalNote, setApprovalNote] = useState<string>('')
  // Body tab selection — "Things to consider" (facts) is the default;
  // "Activity" holds the timeline. Roving focus is driven off these two refs.
  const [activeTab, setActiveTab] = useState<'facts' | 'activity'>('facts')
  const factsTabRef = useRef<HTMLButtonElement | null>(null)
  const activityTabRef = useRef<HTMLButtonElement | null>(null)

  // Close = same behaviour the old ← Back button had: pop history when we can,
  // otherwise fall back to the dashboard. Shared by the X, the Done button,
  // Escape, and a backdrop click.
  //
  // "When we can" means the previous entry is an in-app router navigation —
  // NOT `window.history.length > 1`. A Slack "Open in app" deep link opened
  // signed-out goes /leave → /login → /leave as full-page loads, so the raw
  // browser history has the login page right behind the modal. The router
  // resets its own index on every full page load, so `canGoBack` is false
  // there and Close lands on the dashboard instead of the login form.
  const closeModal = useCallback((): void => {
    if (canGoBack) window.history.back()
    else void navigate({ to: '/' })
  }, [canGoBack, navigate])

  const invalidateLeaveRelatedQueries = (): void => {
    void queryClient.invalidateQueries({ queryKey: ['me'] })
    void queryClient.invalidateQueries({ queryKey: ['leaves'] })
    void queryClient.invalidateQueries({ queryKey: ['requests'] })
    void queryClient.invalidateQueries({ queryKey: ['dashboard'] })
    void queryClient.invalidateQueries({ queryKey: ['calendar'] })
    void queryClient.invalidateQueries({ queryKey: ['shell'] })
    void queryClient.invalidateQueries({ queryKey: ['nav-counts'] })
  }

  useEffect(() => {
    if (!leaveId) {
      setState({ status: 'error', message: 'Missing leave id.' })
      return
    }
    let active = true
    setState({ status: 'loading' })
    getLeaveDetail(leaveId).then(
      (data) => {
        if (active) setState({ status: 'ok', data })
      },
      (err: unknown) => {
        if (!active) return
        const message = err instanceof Error ? err.message : 'Failed to load leave.'
        setState({ status: 'error', message })
      },
    )
    return () => {
      active = false
    }
  }, [leaveId])


  if (state.status === 'loading') {
    return (
      <ModalShell onClose={closeModal}>
        <div data-testid="leave-detail-loading" style={{ padding: 24, display: 'flex', flexDirection: 'column', gap: 12 }}>
          <Skeleton height={64} />
          <Skeleton height={120} />
          <Skeleton height={180} />
        </div>
      </ModalShell>
    )
  }

  if (state.status === 'error') {
    return (
      <ModalShell onClose={closeModal}>
        <div
          data-testid="leave-detail-error"
          role="alert"
          style={{
            margin: 24,
            padding: '14px 16px',
            background: 'var(--danger-bg)',
            border: '1px solid var(--danger-line)',
            borderRadius: 8,
            color: 'var(--danger-fg)',
            fontSize: 14,
          }}
        >
          {state.message}
        </div>
      </ModalShell>
    )
  }

  const { data } = state
  const statusKey = data.status.toLowerCase()
  const isOwnLeave = session.user?.id === data.employee.id
  const viewerRole = session.user?.role ?? ''
  // HR ops (HR + admin/superadmin) run the review process.
  const isHrOps = viewerRole === 'hr' || viewerRole === 'admin' || viewerRole === 'superadmin'
  // Masking is OWNERSHIP-based, not role-based: to the leave OWNER (the
  // requester — even if they're HR), the pending_hr_review holding state reads
  // as plain "pending" all the way. Anyone viewing SOMEONE ELSE's leave (the
  // reviewer/approver context) keeps the true status. DISPLAY-ONLY: statusKey
  // below is unchanged, so every action-gating check sees the real status.
  const displayStatusKey =
    isOwnLeave && statusKey === 'pending_hr_review' ? 'pending' : statusKey
  // HR can act on a document-required leave awaiting review, from the detail
  // modal (opened via the HR Doc Review queue). Never on their OWN leave —
  // segregation of duties: you don't review your own docs.
  const canHrReview =
    flags.hrDocumentReview && isHrOps && !isOwnLeave && statusKey === 'pending_hr_review'
  const statusBadge = STATUS_PALETTE[displayStatusKey] ?? {
    fg: 'var(--text-2)',
    bg: 'var(--surface-2)',
    label: displayStatusKey,
  }
  const canDecide =
    APPROVER_ROLES.has(viewerRole) &&
    !isOwnLeave &&
    statusKey === 'pending'
  // Owner cancellation: any in-flight status, including composite overflow
  // pending states. The BE cascades to the sibling leg automatically when
  // `linked_request_id` is set (services/api/src/domain/leave_decisions.py).
  const CANCELLABLE_STATUSES = new Set([
    'pending',
    'escalated',
    // Owner may withdraw a leave still awaiting HR review (it reads as
    // "Pending" to them). Direct self-cancel, not the request-cancellation flow.
    'pending_hr_review',
  ])
  // Owner may also ask to cancel an APPROVED leave (flag-gated). This is NOT
  // a one-click cancel — it files a request that the manager approves; the
  // dialog collects a required reason and we call request-cancellation.
  const isRequestCancel =
    isOwnLeave &&
    flags.cancelApprovedRequiresApproval &&
    canRequestCancellation({ status: statusKey } as Pick<Leave, 'status'>)
  const canCancel =
    isOwnLeave && (CANCELLABLE_STATUSES.has(statusKey) || isRequestCancel)
  // Manager-side: decide a pending cancellation request on someone else's
  // leave. Same approver roles that can decide a fresh request.
  const canDecideCancellation =
    APPROVER_ROLES.has(viewerRole) &&
    !isOwnLeave &&
    statusKey === 'pending_cancellation'
  // HR (and above) manual override: void someone else's APPROVED leave directly,
  // no request/approval dance. pending_cancellation is handled by the
  // decide-cancellation buttons above. Backed by POST /leaves/{id}/hr-cancel.
  // Gated on `hrCancelApproved` too, so the same kill-switch hides HR-cancel on
  // both the Leaves list and this detail surface (they must stay in lockstep).
  const canHrCancel =
    flags.hrCancelApproved &&
    HR_CANCEL_ROLES.has(viewerRole) &&
    !isOwnLeave &&
    statusKey === 'approved'
  // The cancel dialog collects a required reason for the request flow and the
  // HR override; a plain owner-withdraw of an in-flight leave needs none.
  const needsCancelReason = isRequestCancel || hrCancelMode

  // Slice D — Approve is gated for Annual / Sick leaves that overlap a
  // blackout window. The manager must endorse to Director with a written
  // justification instead. ``team_calendar.blackouts`` carries the list of
  // blackouts the backend already loaded for the calendar strip.
  const _leaveType = data.type as string
  const _isALSL = _leaveType === 'annual' || _leaveType === 'sick'
  const _overlappingBlackout = data.team_calendar.blackouts.find(
    (b) => b.start_date <= data.end_date && b.end_date >= data.start_date,
  )
  // Endorsement only makes sense when the approver sits below Director.
  // If the assigned approver is already a Director / Exec Director, the
  // "Endorse to Director" detour is a no-op — they ARE the Director —
  // so render the plain Approve / Reject pair instead.
  const _viewerIsDirectorTier =
    viewerRole === 'director' || viewerRole === 'exec_director'
  const requiresEndorsement =
    canDecide &&
    _isALSL &&
    statusKey === 'pending' &&
    _overlappingBlackout !== undefined &&
    !_viewerIsDirectorTier

  // RFC 007 — a request that draws unpaid days (a standalone unpaid leave OR an
  // annual request that overflowed its balance) cannot be manager-approved; it
  // must be escalated to the Exec Director. Mirrors the server-side 409 guard
  // (leave_requires_escalation). Blackout overlap is handled by
  // ``requiresEndorsement`` above, so this covers the unpaid case only.
  const _hasUnpaidDays =
    data.type === 'unpaid' || (data.unpaid_days ?? 0) > 0
  const requiresUnpaidEscalation =
    canDecide &&
    statusKey === 'pending' &&
    !_viewerIsDirectorTier &&
    !requiresEndorsement &&
    _hasUnpaidDays

  // Composite manager-recommend CTA — per HR Ops ratification 2026-05-17.
  // ONLY the direct line manager may file the recommendation. HOD /
  // cross-team manager / HR / Director see read-only banners instead.
  // ``primary_approver_id`` is sourced from the resolver
  // (approval_assignments) — the legacy ``reports_to`` column was
  // dropped in alembic 0077.
  const isDirectManager =
    Boolean(session.user?.id) &&
    Boolean(data.employee.primary_approver_id) &&
    session.user?.id === data.employee.primary_approver_id
  // RFC 004 §4.2 / §4.3 manager-recommendation form. This gate
  // covers two standalone flows that don't carry a sibling:
  //   §4.2: annual leave that the employee resubmitted after a blackout
  //         overlap (employee_resubmit_justification is set).
  //   §4.3: standalone unpaid leave (linked_request_id is null AND
  //         type === 'unpaid').
  // Status gate lives inside the form component so the parent doesn't
  // duplicate it; the gate here is policy (which path) + role + linkage.
  // Director cascade decision — when the composite has cleared the manager
  // recommendation step and sits at `pending_director_decision`, a Director
  // (or Exec Director) should be able to approve or reject from the detail
  // page directly, not just from /director/leaves. The BE approve/reject
  // routes already cascade to the linked sibling for this status.
  const isDirectorRole =
    viewerRole === 'director' || viewerRole === 'exec_director'
  const composite = data.composite ?? null
  // A composite request (annual + unpaid overflow pair) carries a linked
  // sibling leg, so the director's decision must cascade to both — that's the
  // "Approve/Reject composite" pair. A plain single escalated leave has no
  // sibling, so those cascade buttons are meaningless. Gate the two decision
  // surfaces on composite presence so they're MUTUALLY EXCLUSIVE — exactly one
  // Approve/Reject pair renders, never both. (Both conditions used to be
  // identical, which is why every escalated leave showed four buttons.)
  const canDirectorDecide =
    isDirectorRole && !isOwnLeave && statusKey === 'escalated' && composite !== null
  // RFC 007 — the Exec Director (or Director) decides an ESCALATED leave
  // directly from the detail page. A manager escalated it here (unpaid /
  // blackout); the top approver approves or rejects. approve_leave_tx already
  // permits the ESCALATED → APPROVED transition, and the 409 escalation guard
  // only gates the PENDING (manager) state, so this never trips it.
  const canDecideEscalated =
    isDirectorRole && !isOwnLeave && statusKey === 'escalated' && composite === null
  // canViewAnalysis: defence-in-depth FE gate. The API may return
  // full_analysis to the leave owner (employee sees their own leave) but
  // the full analysis contains peer names, coverage percentages, and
  // pattern flags that are internal advisor data — not intended for the
  // requesting employee. Only approver roles see the AI analysis panel.
  const canViewAnalysis = APPROVER_ROLES.has(viewerRole) && !isOwnLeave
  const canRedoAnalysis = canViewAnalysis
  // Live capacity verdict from the server (domain/escalation.py::
  // approval_blocked_by_capacity): approving this leave now would push the team
  // over its concurrent-absence cap on some day. Disable Approve (Reject stays
  // enabled) and show the reason. approve_leave_tx enforces the same guard, so
  // this is an affordance, not the security boundary.
  const capacityReached = data.capacity_reached === true
  // Busiest-day snapshot behind capacityReached — normalised to the same shape
  // the /approve 409 returns so the confirm dialog reads one type. Present iff
  // capacityReached. The applicant would make it peakOthers + 1 vs teamCap.
  const detailCapacitySnapshot: CapacityAckSnapshot | null = data.capacity_detail
    ? {
        peakOthers: data.capacity_detail.peak_others,
        teamCap: data.capacity_detail.team_cap,
        day: data.capacity_detail.day,
        capPhrase: data.capacity_detail.cap_phrase,
      }
    : null
  // True when this leave was ultimately approved above capacity — drives the
  // "Above capacity" header badge.
  const approvedOverCapacity = data.approved_over_capacity === true

  const dateRange = formatDateRange(data.start_date, data.end_date)
  // Rhythm now lives on the avatar ring; visible for the owner or an approver.
  const rhythmVisible = Boolean(data.health_bar) && (isOwnLeave || canViewAnalysis)
  const rhythmBand = data.health_bar?.band
  // 2×2 metadata-grid facts.
  const halfDayDetail = deriveHalfDayDetail(data)
  // Day-by-day AM/PM/FULL breakdown — only meaningful for working-day types
  // (they honour weekends/holidays) and only worth showing when the range
  // spans more than one working day or carries a half somewhere.
  const showDayBreakdown =
    (LEAVE_TYPE_HALF_DAY_ALLOWED[data.type as LeaveTypeId] ?? false) &&
    (data.start_date !== data.end_date ||
      data.half_day ||
      data.half_start ||
      data.half_end ||
      (data.half_days?.length ?? 0) > 0)
  const reasonText =
    data.reason && data.reason.trim().length > 0
      ? `“${data.reason.trim()}”`
      : '(no reason provided)'

  // Roving-focus keyboard nav for the two-tab control. Arrows / Home / End
  // move focus and activate (automatic-activation pattern); Enter / Space fall
  // through to the button's native click, which also activates.
  const onTabKeyDown = (event: React.KeyboardEvent<HTMLButtonElement>): void => {
    let target: 'facts' | 'activity' | null = null
    if (event.key === 'Home') target = 'facts'
    else if (event.key === 'End') target = 'activity'
    else if (event.key === 'ArrowRight')
      target = activeTab === 'facts' ? 'activity' : 'facts'
    else if (event.key === 'ArrowLeft')
      target = activeTab === 'activity' ? 'facts' : 'activity'
    if (target === null) return
    event.preventDefault()
    setActiveTab(target)
    ;(target === 'facts' ? factsTabRef : activityTabRef).current?.focus()
  }

  const tabStyle = (tab: 'facts' | 'activity'): React.CSSProperties => ({
    padding: '8px 14px',
    minHeight: 36,
    background: 'transparent',
    border: 'none',
    borderBottom:
      activeTab === tab ? '2px solid var(--accent)' : '2px solid transparent',
    color: activeTab === tab ? 'var(--text)' : 'var(--text-3)',
    fontSize: 13,
    fontWeight: 600,
    cursor: 'pointer',
    marginBottom: -1,
  })

  // Core approve. ``overCapacityAck`` is set only after the manager confirms the
  // over-capacity dialog. Without it, an over-cap approval 409s with a snapshot
  // (capacityAckRequired) — we catch that and open the confirm dialog rather
  // than surfacing an error, so the server is the authoritative gate even if the
  // detail payload was stale (a teammate's leave landing after this page loaded).
  const handleApprove = async (overCapacityAck = false): Promise<void> => {
    if (!leaveId) return
    // The note is optional for routine approvals (zero-friction) but REQUIRED
    // when approving above capacity — the confirm modal captures it and its
    // button is disabled while empty; this guard is the belt-and-braces.
    const note = approvalNote.trim()
    if (overCapacityAck && note.length === 0) {
      setCapacityConfirmError('A reason is required to approve above capacity.')
      return
    }
    setBusy(true)
    try {
      await approveLeave(
        leaveId,
        note.length > 0 ? note : undefined,
        overCapacityAck ? { overCapacityAck: true } : undefined,
      )
      const fresh = await getLeaveDetail(leaveId)
      setState({ status: 'ok', data: fresh })
      invalidateLeaveRelatedQueries()
      setApprovalNote('')
      setCapacityConfirmOpen(false)
      notify(
        overCapacityAck ? 'Leave approved above capacity' : 'Leave approved',
        'success',
      )
    } catch (err: unknown) {
      const snapshot = capacityAckRequired(err)
      if (snapshot && !overCapacityAck) {
        // Above capacity and not yet confirmed — raise the confirm dialog.
        setCapacitySnapshot(snapshot)
        setCapacityConfirmError(null)
        setCapacityConfirmOpen(true)
        return
      }
      // E1: never let an approval silently fail — tell the user.
      const message =
        err instanceof Error ? err.message : 'Failed to approve leave.'
      if (capacityConfirmOpen) setCapacityConfirmError(message)
      else notify(message, 'error')
    } finally {
      setBusy(false)
    }
  }

  // Approve click. When the detail already tells us this approval is over
  // capacity, open the confirm dialog with that snapshot up front; otherwise
  // approve directly and let the server 409 raise the dialog on a race.
  const handleApproveClick = (): void => {
    if (!leaveId) return
    if (capacityReached && detailCapacitySnapshot) {
      setCapacitySnapshot(detailCapacitySnapshot)
      setCapacityConfirmError(null)
      setCapacityConfirmOpen(true)
      return
    }
    void handleApprove(false)
  }

  // Manager confirmed the over-capacity dialog — approve with the ack.
  const confirmApproveOverCapacity = (): void => {
    void handleApprove(true)
  }

  const openRejectDialog = (): void => {
    setRejectReason('')
    setRejectError(null)
    setRejectOpen(true)
  }

  const closeRejectDialog = (): void => {
    setRejectOpen(false)
    setRejectError(null)
  }

  const confirmReject = async (): Promise<void> => {
    if (!leaveId) return
    const trimmed = rejectReason.trim()
    if (!trimmed) return
    setBusy(true)
    setRejectError(null)
    try {
      await rejectLeave(leaveId, trimmed)
      const fresh = await getLeaveDetail(leaveId)
      setState({ status: 'ok', data: fresh })
      invalidateLeaveRelatedQueries()
      setRejectOpen(false)
      setRejectReason('')
    } catch (err: unknown) {
      setRejectError(err instanceof Error ? err.message : 'Failed to reject leave.')
    } finally {
      setBusy(false)
    }
  }

  const handleDirectorDecisionSubmit = async (): Promise<void> => {
    if (!leaveId || directorDecisionMode === null) return
    setDirectorDecisionPending(true)
    setDirectorDecisionError(null)
    try {
      const note = directorDecisionNote.trim()
      if (directorDecisionMode === 'approve') {
        await approveLeave(leaveId, note)
      } else {
        await rejectLeave(leaveId, note)
      }
      const fresh = await getLeaveDetail(leaveId)
      setState({ status: 'ok', data: fresh })
      invalidateLeaveRelatedQueries()
      setDirectorDecisionMode(null)
      setDirectorDecisionNote('')
    } catch (err: unknown) {
      setDirectorDecisionError(
        err instanceof Error
          ? err.message
          : `Failed to ${directorDecisionMode} the composite.`,
      )
    } finally {
      setDirectorDecisionPending(false)
    }
  }

  const handleCancel = async (withReason?: string): Promise<void> => {
    if (!leaveId) return
    const hrOverride = hrCancelMode
    setCancelOpen(false)
    setHrCancelMode(false)
    setBusy(true)
    try {
      if (hrOverride) {
        // HR manual override — void the approved leave directly.
        await cancelHrLeave(leaveId, (withReason ?? '').trim())
      } else if (withReason !== undefined) {
        // An approved leave files a cancellation request (→ manager).
        await requestLeaveCancellation(leaveId, withReason)
      } else {
        // Any other cancellable status is withdrawn directly.
        await cancelLeave(leaveId)
      }
      const fresh = await getLeaveDetail(leaveId)
      setState({ status: 'ok', data: fresh })
      setCancelReason('')
      invalidateLeaveRelatedQueries()
    } catch (err: unknown) {
      notify(err instanceof Error ? err.message : 'Failed to cancel leave.', 'error')
    } finally {
      setBusy(false)
    }
  }

  const handleDecideCancellation = async (
    approve: boolean,
    declineReason?: string,
  ): Promise<void> => {
    if (!leaveId) return
    setBusy(true)
    try {
      if (approve) {
        await approveCancellation(leaveId)
      } else {
        await rejectCancellation(leaveId, declineReason)
      }
      const fresh = await getLeaveDetail(leaveId)
      setState({ status: 'ok', data: fresh })
      invalidateLeaveRelatedQueries()
    } catch (err: unknown) {
      notify(
        err instanceof Error ? err.message : 'Failed to record the cancellation decision.',
        'error',
      )
    } finally {
      setBusy(false)
    }
  }

  return (
    <>
      <ModalShell onClose={closeModal} cardTestId="leave-detail-page">
        {/* ── Header (fixed, non-scrolling) ── */}
        <div
          style={{
            flexShrink: 0,
            position: 'relative',
            display: 'flex',
            flexDirection: 'column',
            gap: 8,
            padding: '20px 24px 16px',
            borderBottom: '1px solid var(--border)',
          }}
        >
          <button
            type="button"
            aria-label="Close"
            data-testid="leave-detail-close"
            onClick={closeModal}
            style={{
              position: 'absolute',
              top: 12,
              right: 12,
              width: 36,
              height: 36,
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
              background: 'transparent',
              border: 'none',
              borderRadius: 8,
              color: 'var(--text-2)',
              cursor: 'pointer',
            }}
          >
            <Icon name="close" size={20} />
          </button>
          <div style={{ display: 'flex', alignItems: 'center', gap: 10, flexWrap: 'wrap', paddingRight: 44 }}>
            {data.is_emergency ? (
              <Tooltip content="Annual leave filed with less than 7 days notice is classified as Emergency Leave (Policy §1.1). It deducts from the annual balance and counts toward the 3-per-year Poor Planning threshold.">
                <span
                  data-testid="leave-emergency-badge"
                  style={{
                    ...HEADER_CHIP_STYLE,
                    color: 'var(--warning-fg)',
                    background: 'var(--warning-bg)',
                    cursor: 'help',
                  }}
                >
                  Emergency
                </span>
              </Tooltip>
            ) : null}
            {data.is_out_of_system ? (
              <Tooltip content="Encoded by HR out of system (approved offline, e.g. unpaid or blackout-period leave).">
                <span
                  data-testid="leave-out-of-system-badge"
                  style={{
                    ...HEADER_CHIP_STYLE,
                    color: 'var(--info-fg)',
                    background: 'var(--info-bg)',
                    cursor: 'help',
                  }}
                >
                  Out of system
                </span>
              </Tooltip>
            ) : null}
            {approvedOverCapacity ? (
              <Tooltip content="Approved while the team was over its concurrent-absence cap. Recorded for HR oversight.">
                <span
                  data-testid="leave-over-capacity-badge"
                  style={{
                    ...HEADER_CHIP_STYLE,
                    color: 'var(--warning-fg, var(--danger-fg))',
                    background: 'var(--warning-bg, var(--danger-bg))',
                    cursor: 'help',
                  }}
                >
                  Above capacity
                </span>
              </Tooltip>
            ) : null}
            <span
              data-testid="leave-status-pill"
              style={{
                ...HEADER_CHIP_STYLE,
                color: statusBadge.fg,
                background: statusBadge.bg,
              }}
            >
              {statusBadge.label}
            </span>
          </div>
          {/* Person row — rounded-square avatar + name (the heading) with a
              team · leave-type subtitle. When the rhythm is visible the avatar
              is tinted by the band (initials, not a glyph — matching the
              Rhythm board); otherwise a plain name-coloured square. */}
          <div style={{ display: 'flex', alignItems: 'center', gap: 12, marginTop: 2 }}>
            <span data-testid="leave-detail-avatar" style={{ flexShrink: 0 }}>
              {rhythmVisible && rhythmBand ? (
                <RhythmAvatar
                  name={displayName(data.employee)}
                  band={rhythmBand}
                  patternCount={data.health_bar?.contributing_patterns.length ?? 0}
                  size={44}
                />
              ) : (
                <Avatar name={displayName(data.employee)} size={44} />
              )}
            </span>
            <div style={{ minWidth: 0 }}>
              <h1 style={{ margin: 0, fontSize: 20, fontWeight: 700, color: 'var(--text)' }}>
                {displayName(data.employee)}
              </h1>
              <div style={{ fontSize: 13, color: 'var(--text-3)' }}>
                {data.employee.team_name} · {leaveTypeLabel(data.type)}
              </div>
            </div>
          </div>

          {/* Metadata — three boxed cells in one row (Dates · Balance after ·
              Leave rhythm), split by thin dividers. Pinned with the header so
              the key facts stay visible while the body scrolls. */}
          <Card style={{ borderColor: 'var(--border-strong)', marginTop: 8 }} padding={0}>
            <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr 1fr' }}>
              <div style={{ padding: '12px 16px', borderRight: '1px solid var(--border)' }}>
                <MetaColumn label="Dates">
                  {dateRange}
                  <span style={META_SUBLINE_STYLE}>
                    {data.days} day{data.days === 1 ? '' : 's'}
                    {halfDayDetail ? ` · ${halfDayDetail}` : ''}
                  </span>
                </MetaColumn>
              </div>
              <div style={{ padding: '12px 16px', borderRight: '1px solid var(--border)' }}>
                <MetaColumn label="Balance after">
                  {data.type === 'unpaid'
                    ? 'No balance'
                    : data.balance_after != null
                      ? `${data.balance_after} days`
                      : '—'}
                  {data.type === 'unpaid' ? (
                    <span style={META_SUBLINE_STYLE}>unpaid leave</span>
                  ) : null}
                </MetaColumn>
              </div>
              <div style={{ padding: '12px 16px' }}>
                <MetaColumn label="Leave rhythm">
                  {rhythmVisible && rhythmBand ? (
                    <span style={{ display: 'inline-flex', alignItems: 'center', gap: 6 }}>
                      <span
                        aria-hidden
                        style={{ color: RHYTHM_STYLE[rhythmBand].ring, fontWeight: 700, flexShrink: 0 }}
                      >
                        {RHYTHM_STYLE[rhythmBand].icon}
                      </span>
                      <span style={{ color: RHYTHM_STYLE[rhythmBand].ring }}>
                        {RHYTHM_STYLE[rhythmBand].label}
                      </span>
                    </span>
                  ) : (
                    '—'
                  )}
                  {rhythmVisible && data.health_bar ? (
                    <span style={META_SUBLINE_STYLE}>
                      {data.health_bar.contributing_patterns.length > 0
                        ? data.health_bar.contributing_patterns
                            .map(humanisePatternCode)
                            .join(' · ')
                        : 'No active patterns'}
                    </span>
                  ) : null}
                </MetaColumn>
              </div>
            </div>
          </Card>

          {data.composite ? (
            <CompositeRequestBlock
              siblingId={data.composite.sibling_id}
              thisTypeLabel={data.composite.this_type_label}
              thisDays={data.composite.this_days}
              siblingTypeLabel={data.composite.sibling_type_label}
              siblingDays={data.composite.sibling_days}
              totalDays={data.composite.total_days}
            />
          ) : null}
        </div>

        {/* ── Body (scrollable) ── */}
        <div
          style={{
            flex: 1,
            minHeight: 0,
            overflowY: 'auto',
            display: 'flex',
            flexDirection: 'column',
            gap: 16,
            padding: '16px 24px',
          }}
        >
          {/* Approver path — the fixed approval chain (Requester → Manager →
              Exec Director when escalated) with live per-node state, so an
              approver sees who has acted and who decides next. */}
          <ApproverPath
            leave={data}
            isOwnLeave={isOwnLeave}
            viewerName={session.user?.name ?? null}
          />

          {/* Day-by-day breakdown — each working day marked FULL / ½ AM / ½ PM,
              read from the per-date slots already on the detail payload. */}
          {showDayBreakdown ? (
            <div data-testid="leave-detail-day-breakdown" style={{ minWidth: 0 }}>
              <Collapsible
                trigger={
                  <span
                    style={{
                      fontSize: 11,
                      fontWeight: 700,
                      letterSpacing: '0.06em',
                      textTransform: 'uppercase',
                      color: 'var(--text-2)',
                    }}
                  >
                    Day-by-day
                  </span>
                }
              >
                <Card padding={4} style={{ borderColor: 'var(--border)' }}>
                  <LeaveDayBreakdownFromDetail detail={data} />
                </Card>
              </Collapsible>
            </div>
          ) : null}

          {/* Reason — scrolls with the body (not pinned in the header). */}
          <div style={{ display: 'flex', flexDirection: 'column', gap: 4, minWidth: 0 }}>
            <span
              style={{
                fontSize: 11,
                fontWeight: 700,
                letterSpacing: '0.06em',
                textTransform: 'uppercase',
                color: 'var(--text-2)',
              }}
            >
              Reason
            </span>
            <p
              data-testid="leave-reason"
              style={{
                margin: 0,
                fontSize: 13,
                fontStyle: 'italic',
                color: 'var(--text)',
              }}
            >
              {reasonText}
            </p>
          </div>
          {data.documents.length > 0 ? (
            <div style={{ display: 'flex', flexDirection: 'column', gap: 4, minWidth: 0 }}>
              <span
                style={{
                  fontSize: 11,
                  fontWeight: 700,
                  letterSpacing: '0.06em',
                  textTransform: 'uppercase',
                  color: 'var(--text-2)',
                }}
              >
                {data.documents.length > 1 ? 'Attachments' : 'Attachment'}
              </span>
              <div
                data-testid="leave-detail-documents"
                style={{ display: 'flex', flexDirection: 'column', gap: 2 }}
              >
                {data.documents.map((doc) => (
                  <DocumentAttachmentRow
                    key={doc.id}
                    doc={doc}
                    onPreview={() => setPreviewDoc(doc)}
                  />
                ))}
              </div>
            </div>
          ) : null}

          {/* Owner can attach a document while the leave awaits HR review — the
              way to respond to a "please update your request" ask. Uses the real
              status (not the masked one); shown to the owner only. */}
          {isOwnLeave && statusKey === 'pending_hr_review' ? (
            <div style={{ marginTop: data.documents.length > 0 ? 8 : 0 }}>
              <LeaveDocumentUpload
                leaveId={data.id}
                onUploaded={async () => {
                  if (!leaveId) return
                  const fresh = await getLeaveDetail(leaveId)
                  setState({ status: 'ok', data: fresh })
                  invalidateLeaveRelatedQueries()
                }}
              />
            </div>
          ) : null}

          {/* RFC 004 §4.2 / §4.3 — Manager files a recommendation for the
              Director. Only renders for the two non-composite flows (linked
              composite overflow uses the existing /recommend modal lower in
              the page). */}
          {isOwnLeave ? (
            <BlackoutResetBanner
              leaveId={data.id}
              status={data.status.toLowerCase()}
              pendingReason={data.pending_reason ?? null}
              startDate={data.start_date}
              endDate={data.end_date}
            />
          ) : null}

      {/* Manager escalation note (RFC 007). Visible to ALL roles: it's the
          audit artifact that justifies forwarding a request to the Exec
          Director, and the employee whose leave it concerns has a right to
          see the manager's wording verbatim (HR Ops ratification 2026-05-17). */}
      {data.manager_justification ? (
        <Card>
          <h2
            style={{
              margin: '0 0 8px',
              fontSize: 11,
              fontWeight: 700,
              color: 'var(--text-2)',
              textTransform: 'uppercase',
              letterSpacing: '0.06em',
            }}
          >
            Approver's note
          </h2>
          <div style={{ fontSize: 12, color: 'var(--text-3)', marginBottom: 8 }}>
            Recorded by the line manager when escalating this request.
          </div>
          <blockquote
            data-testid="leave-detail-recommendation-note"
            style={{
              margin: 0,
              padding: '10px 14px',
              borderLeft: '3px solid var(--steel-blue)',
              background: 'var(--surface-2)',
              borderRadius: 4,
              fontSize: 14,
              color: 'var(--text)',
              lineHeight: 1.55,
              whiteSpace: 'pre-wrap',
            }}
          >
            {data.manager_justification}
          </blockquote>
        </Card>
      ) : null}

      {/* Team coverage — borderless section: a label (like Reason) above the
          grid, no card frame. */}
      <div>
        <h2
          style={{
            margin: '0 0 10px',
            fontSize: 11,
            fontWeight: 700,
            color: 'var(--text-2)',
            textTransform: 'uppercase',
            letterSpacing: '0.06em',
          }}
        >
          Team coverage during this leave
        </h2>
        <LeaveTeamCalendar
          data={data.team_calendar}
          viewerIsOwner={isOwnLeave}
          {...(rhythmBand
            ? { requesterBorderColor: RHYTHM_STYLE[rhythmBand].ring }
            : {})}
        />
      </div>

          {/* Read-only banner for the direct manager once the recommendation
              is filed. */}
          {isDirectManager && statusKey === 'escalated' ? (
            <Card>
              <div
                data-testid="leave-detail-awaiting-director"
                style={{
                  padding: 4,
                  fontSize: 13,
                  color: 'var(--text-2)',
                }}
              >
                <strong>Your note is with the Director.</strong>{' '}
                {data.linked_request_id != null
                  ? 'They will decide both legs of this composite request together.'
                  : "They'll decide soon — you don't need to do anything else here."}
              </div>
            </Card>
          ) : null}

          {/* Two-tab control — "Things to consider" (deterministic facts) is
              the default; "Activity" holds the audit timeline. Shown to every
              viewer, including approvers. */}
          <div>
            <div
              role="tablist"
              aria-label="Leave detail sections"
              style={{
                display: 'flex',
                gap: 4,
                borderBottom: '1px solid var(--border)',
                marginBottom: 12,
              }}
            >
              <button
                type="button"
                role="tab"
                id="leave-detail-tab-facts"
                data-testid="detail-tab-facts"
                ref={factsTabRef}
                aria-selected={activeTab === 'facts'}
                aria-controls="leave-detail-panel-facts"
                tabIndex={activeTab === 'facts' ? 0 : -1}
                onClick={() => setActiveTab('facts')}
                onKeyDown={onTabKeyDown}
                style={tabStyle('facts')}
              >
                Things to consider
              </button>
              <button
                type="button"
                role="tab"
                id="leave-detail-tab-activity"
                data-testid="detail-tab-activity"
                ref={activityTabRef}
                aria-selected={activeTab === 'activity'}
                aria-controls="leave-detail-panel-activity"
                tabIndex={activeTab === 'activity' ? 0 : -1}
                onClick={() => setActiveTab('activity')}
                onKeyDown={onTabKeyDown}
                style={tabStyle('activity')}
              >
                Activity
              </button>
            </div>
            <FadeIn triggerKey={activeTab} duration="fast">
              {activeTab === 'facts' ? (
                <div
                  role="tabpanel"
                  id="leave-detail-panel-facts"
                  aria-labelledby="leave-detail-tab-facts"
                >
                  {/* "At a glance" facts panel — the deterministic checklist
                      (duration, coverage, blackout, pattern, unpaid) that
                      replaced the dark ILMU assistant. Reads the live rule
                      preview + the team_calendar. */}
                  <ImpactPreviewSection
                    leaveId={data.id}
                    leave={data}
                    viewerRole={
                      flags.decisionSurfaceV2 && canViewAnalysis
                        ? normaliseImpactViewerRole(viewerRole)
                        : 'employee'
                    }
                    viewerIsOwner={isOwnLeave}
                  />
                </div>
              ) : (
                <div
                  role="tabpanel"
                  id="leave-detail-panel-activity"
                  aria-labelledby="leave-detail-tab-activity"
                >
                  <ActivityTimeline
                    timeline={data.timeline}
                    employeeName={data.employee.name}
                    leaveType={data.type}
                    days={data.days}
                  />
                </div>
              )}
            </FadeIn>
          </div>
        </div>

        {/* ── Footer (fixed) — only rendered when there's an action to take;
            closing the modal is the top-right ✕, so there's no Done button.
            canHrCancel is the HR-override void on an approved leave — without
            it here the whole footer (and its Cancel button) never renders. ── */}
        {(canDecide || canCancel || canDirectorDecide || canDecideEscalated || canDecideCancellation || canHrCancel || canHrReview) ? (
        <div
          style={{
            flexShrink: 0,
            borderTop: '1px solid var(--border)',
            padding: '12px 24px',
            display: 'flex',
            justifyContent: 'flex-end',
            gap: 16,
            flexWrap: 'wrap',
          }}
        >
          <div
            style={{
              display: 'flex',
              flexDirection: 'column',
              gap: 10,
              alignItems: 'stretch',
              minWidth: 0,
              flex: 1,
            }}
          >
      {(canDecide ||
        canCancel ||
        canDirectorDecide ||
        canDecideEscalated ||
        canDecideCancellation ||
        canHrCancel ||
        canHrReview) ? (
        <div style={{ display: 'flex', flexDirection: 'column', gap: 8 }}>
          {canHrReview ? (
            <div style={{ margin: '0 0 4px' }}>
              <p style={{ margin: '0 0 10px', fontSize: 12, color: 'var(--text-3)' }}>
                This request needs HR review before it reaches the approver.
                Forward it to send it on, reject it, or ask {data.employee.name}
                {' '}for a document.
              </p>
              <HrReviewActions
                leaveId={data.id}
                employeeName={data.employee.name}
                onDone={async () => {
                  if (!leaveId) return
                  const fresh = await getLeaveDetail(leaveId)
                  setState({ status: 'ok', data: fresh })
                  invalidateLeaveRelatedQueries()
                }}
              />
            </div>
          ) : null}
          {canDecideCancellation ? (
            <div style={{ margin: '0 0 12px' }}>
              <p style={{ margin: '0 0 8px', fontSize: 12, color: 'var(--text-3)' }}>
                {data.employee.name} asked to cancel this approved leave.
                Approving returns the days to their balance; keeping it leaves
                the leave in place.
              </p>
              {data.cancel_reason ? (
                <p
                  style={{
                    margin: '0 0 12px',
                    padding: '8px 10px',
                    background: 'var(--surface-2)',
                    borderRadius: 8,
                    fontSize: 13,
                    color: 'var(--text-1)',
                  }}
                >
                  <strong>Their reason:</strong> {data.cancel_reason}
                </p>
              ) : null}
              <div style={{ display: 'flex', gap: 10, flexWrap: 'wrap', justifyContent: 'flex-end' }}>
                <button
                  type="button"
                  data-testid="leave-detail-approve-cancellation"
                  disabled={busy}
                  onClick={() => void handleDecideCancellation(true)}
                  style={{
                    padding: '10px 20px',
                    background: 'var(--accent)',
                    color: 'white',
                    border: 'none',
                    borderRadius: 6,
                    fontSize: 14,
                    fontWeight: 600,
                    cursor: busy ? 'not-allowed' : 'pointer',
                    opacity: busy ? 0.6 : 1,
                    minHeight: 44,
                  }}
                >
                  Approve cancellation
                </button>
                <button
                  type="button"
                  data-testid="leave-detail-reject-cancellation"
                  disabled={busy}
                  onClick={() => void handleDecideCancellation(false)}
                  style={{
                    padding: '10px 20px',
                    background: 'transparent',
                    color: 'var(--text-1)',
                    border: '1px solid var(--surface-line)',
                    borderRadius: 6,
                    fontSize: 14,
                    fontWeight: 600,
                    cursor: busy ? 'not-allowed' : 'pointer',
                    opacity: busy ? 0.6 : 1,
                    minHeight: 44,
                  }}
                >
                  Keep leave
                </button>
              </div>
            </div>
          ) : null}
          {/* Over-capacity approvals capture a REQUIRED reason in the confirm
              modal instead, so hide this optional field when over cap to avoid
              two note inputs (both bind to the same approvalNote state). */}
          {(canDecide || canDecideEscalated) && !capacityReached ? (
            <div style={{ margin: '0 0 12px' }}>
              <label
                htmlFor="leave-detail-approval-note"
                style={{
                  display: 'block',
                  fontSize: 12,
                  fontWeight: 600,
                  color: 'var(--text-2)',
                  marginBottom: 6,
                }}
              >
                Approval note{' '}
                <span style={{ fontWeight: 400, color: 'var(--text-3)' }}>(optional)</span>
              </label>
              <textarea
                id="leave-detail-approval-note"
                data-testid="leave-detail-approval-note"
                value={approvalNote}
                onChange={(e) => setApprovalNote(e.target.value)}
                maxLength={APPROVAL_NOTE_MAX}
                rows={2}
                disabled={busy}
                placeholder="Add a note. It is recorded on the activity log when you approve."
                style={{
                  width: '100%',
                  boxSizing: 'border-box',
                  padding: '8px 10px',
                  border: '1px solid var(--border)',
                  borderRadius: 6,
                  fontSize: 13,
                  fontFamily: 'inherit',
                  resize: 'vertical',
                  color: 'var(--text-1)',
                  background: 'var(--surface-1, white)',
                }}
              />
            </div>
          ) : null}
          {canDirectorDecide ? (
            <p style={{ margin: '0 0 12px', fontSize: 12, color: 'var(--text-3)' }}>
              {data.manager_justification ? (
                <>
                  The line manager escalated this request. Your decision{' '}
                  {composite
                    ? `applies to both legs — the ${formatCompositeDays(composite.this_days)}-day ${composite.this_type_label} leg and the ${formatCompositeDays(composite.sibling_days)}-day ${composite.sibling_type_label} leg`
                    : 'finalises this leave'}
                  . Approve or reject; a decision note is required for the
                  audit trail.
                </>
              ) : (
                'This request is awaiting your decision. Approve or reject; a decision note is required for the audit trail.'
              )}
            </p>
          ) : null}
          <div
            style={{
              display: 'flex',
              gap: 8,
              flexWrap: 'wrap',
              alignItems: 'center',
              justifyContent: 'flex-end',
            }}
          >
            {canDecide || canDecideEscalated ? (
                <>
                  {/* Over-capacity warning sits on the SAME row as the buttons,
                      pushed left by marginRight:auto (buttons stay right). */}
                  {capacityReached ? (
                    <p
                      data-testid="leave-detail-capacity-warning"
                      role="status"
                      style={{
                        display: 'flex',
                        alignItems: 'center',
                        gap: 6,
                        marginTop: 0,
                        marginBottom: 0,
                        marginLeft: 0,
                        marginRight: 'auto',
                        color: 'var(--danger-fg)',
                        fontSize: 13,
                        fontWeight: 600,
                      }}
                    >
                      <svg
                        width="16"
                        height="16"
                        viewBox="0 0 24 24"
                        fill="none"
                        aria-hidden="true"
                        style={{ flexShrink: 0 }}
                      >
                        <path
                          d="M12 3.2 1.6 21.2h20.8L12 3.2Z"
                          fill="currentColor"
                          opacity="0.16"
                        />
                        <path
                          d="M12 2.2a1.3 1.3 0 0 1 1.13.66l9.35 16.2A1.3 1.3 0 0 1 21.35 21H2.65a1.3 1.3 0 0 1-1.13-1.94l9.35-16.2A1.3 1.3 0 0 1 12 2.2Zm0 5.6a.85.85 0 0 0-.85.85v4.6a.85.85 0 0 0 1.7 0v-4.6A.85.85 0 0 0 12 7.8Zm0 8.1a1.05 1.05 0 1 0 0 2.1 1.05 1.05 0 0 0 0-2.1Z"
                          fill="currentColor"
                        />
                      </svg>
                      <span>
                        {detailCapacitySnapshot
                          ? `Approving puts the team above capacity: ${
                              detailCapacitySnapshot.peakOthers + 1
                            } of ${detailCapacitySnapshot.teamCap} (${
                              detailCapacitySnapshot.capPhrase
                            }).`
                          : 'Approving this leave puts the team above capacity.'}
                      </span>
                    </p>
                  ) : null}
                  {requiresEndorsement && _overlappingBlackout ? (
                    <EndorseToDirector
                      leaveId={data.id}
                      blackoutName={_overlappingBlackout.name}
                      disabled={busy}
                      onSuccess={() => {
                        void (async () => {
                          if (!leaveId) return
                          setBusy(true)
                          try {
                            const fresh = await getLeaveDetail(leaveId)
                            setState({ status: 'ok', data: fresh })
                            invalidateLeaveRelatedQueries()
                          } finally {
                            setBusy(false)
                          }
                        })()
                      }}
                    />
                  ) : requiresUnpaidEscalation ? (
                    <EndorseToDirector
                      leaveId={data.id}
                      cta="Escalate to Exec Director"
                      reasonText="This request includes unpaid days, so it can't be approved directly — it needs Executive Director sign-off. Write a short justification, then escalate."
                      disabled={busy}
                      onSuccess={() => {
                        void (async () => {
                          if (!leaveId) return
                          setBusy(true)
                          try {
                            const fresh = await getLeaveDetail(leaveId)
                            setState({ status: 'ok', data: fresh })
                            invalidateLeaveRelatedQueries()
                          } finally {
                            setBusy(false)
                          }
                        })()
                      }}
                    />
                  ) : (
                    <button
                      type="button"
                      data-testid="leave-detail-approve"
                      disabled={busy}
                      onClick={() => void handleApproveClick()}
                      title={
                        capacityReached
                          ? 'Approving this leave puts the team above capacity — you will be asked to confirm.'
                          : undefined
                      }
                      aria-disabled={busy}
                      style={{
                        padding: '10px 20px',
                        background: 'var(--success)',
                        color: 'white',
                        border: 'none',
                        borderRadius: 6,
                        fontSize: 14,
                        fontWeight: 600,
                        cursor: busy ? 'not-allowed' : 'pointer',
                        opacity: busy ? 0.6 : 1,
                        minHeight: 44,
                      }}
                    >
                      ✓ Approve
                    </button>
                  )}
                  <button
                    type="button"
                    data-testid="leave-detail-reject"
                    disabled={busy}
                    onClick={openRejectDialog}
                    style={{
                      padding: '10px 20px',
                      background: 'transparent',
                      color: 'var(--danger-fg)',
                      border: '1px solid var(--danger-line)',
                      borderRadius: 6,
                      fontSize: 14,
                      fontWeight: 600,
                      cursor: busy ? 'not-allowed' : 'pointer',
                      opacity: busy ? 0.6 : 1,
                      minHeight: 44,
                    }}
                  >
                    Reject
                  </button>
                </>
            ) : null}
            {canCancel ? (
              <button
                type="button"
                data-testid="leave-detail-cancel"
                disabled={busy}
                onClick={() => {
                  setCancelReason('')
                  setCancelOpen(true)
                }}
                style={{
                  padding: '10px 20px',
                  background: 'transparent',
                  color: 'var(--danger-fg)',
                  border: '1px solid var(--danger-line)',
                  borderRadius: 6,
                  fontSize: 14,
                  fontWeight: 600,
                  cursor: busy ? 'not-allowed' : 'pointer',
                  opacity: busy ? 0.6 : 1,
                  minHeight: 44,
                }}
              >
                Cancel leave request
              </button>
            ) : null}
            {canHrCancel ? (
              <button
                type="button"
                data-testid="leave-detail-hr-cancel"
                title="Only HR can cancel approved leaves"
                disabled={busy}
                onClick={() => {
                  setCancelReason('')
                  setHrCancelMode(true)
                  setCancelOpen(true)
                }}
                style={{
                  padding: '10px 20px',
                  background: 'transparent',
                  color: 'var(--danger-fg)',
                  border: '1px solid var(--danger-line)',
                  borderRadius: 6,
                  fontSize: 14,
                  fontWeight: 600,
                  cursor: busy ? 'not-allowed' : 'pointer',
                  opacity: busy ? 0.6 : 1,
                  minHeight: 44,
                }}
              >
                Cancel leave
              </button>
            ) : null}
            {canDirectorDecide ? (
              <>
                <button
                  type="button"
                  data-testid="leave-detail-director-approve"
                  disabled={directorDecisionPending}
                  onClick={() => {
                    setDirectorDecisionError(null)
                    setDirectorDecisionNote('')
                    setDirectorDecisionMode('approve')
                  }}
                  style={{
                    padding: '10px 20px',
                    background: 'var(--success)',
                    color: 'white',
                    border: 'none',
                    borderRadius: 6,
                    fontSize: 14,
                    fontWeight: 600,
                    cursor: directorDecisionPending ? 'not-allowed' : 'pointer',
                    opacity: directorDecisionPending ? 0.6 : 1,
                    minHeight: 44,
                  }}
                >
                  ✓ Approve composite
                </button>
                <button
                  type="button"
                  data-testid="leave-detail-director-reject"
                  disabled={directorDecisionPending}
                  onClick={() => {
                    setDirectorDecisionError(null)
                    setDirectorDecisionNote('')
                    setDirectorDecisionMode('reject')
                  }}
                  style={{
                    padding: '10px 20px',
                    background: 'transparent',
                    color: 'var(--danger-fg)',
                    border: '1px solid var(--danger-line)',
                    borderRadius: 6,
                    fontSize: 14,
                    fontWeight: 600,
                    cursor: directorDecisionPending ? 'not-allowed' : 'pointer',
                    opacity: directorDecisionPending ? 0.6 : 1,
                    minHeight: 44,
                  }}
                >
                  Reject composite
                </button>
              </>
            ) : null}
          </div>
        </div>
      ) : null}
          </div>
        </div>
        ) : null}
      </ModalShell>
      <ConfirmationDialog
        open={cancelOpen}
        onClose={() => {
          setCancelOpen(false)
          setCancelReason('')
          setHrCancelMode(false)
        }}
        onConfirm={() =>
          void handleCancel(needsCancelReason ? cancelReason.trim() : undefined)
        }
        confirmDisabled={
          needsCancelReason &&
          cancelReason.trim().length <
            (hrCancelMode ? HR_CANCEL_REASON_MIN : CANCEL_REQUEST_REASON_MIN)
        }
        title={
          hrCancelMode
            ? 'Cancel this approved leave?'
            : isRequestCancel
              ? 'Request cancellation?'
              : 'Cancel leave request?'
        }
        subtitle={`${leaveTypeLabel(data.type)} · ${dateRange}`}
        confirmLabel={
          hrCancelMode ? 'Cancel leave' : isRequestCancel ? 'Send request' : 'Cancel request'
        }
        cancelLabel={
          hrCancelMode ? 'Keep leave' : isRequestCancel ? 'Never mind' : 'Keep request'
        }
        variant="danger"
        loading={busy}
        data-testid="leave-detail-cancel-confirm"
      >
        {needsCancelReason ? (
          <>
            <p style={{ margin: 0 }}>
              {hrCancelMode
                ? `This voids ${displayName(data.employee)}'s approved leave and refunds the taken days to their balance. They'll be notified. This can't be undone.`
                : "This leave is already approved. To cancel it we'll send a request to your approver. The days stay booked until they approve. You'll be notified either way."}
            </p>
            <label style={{ display: 'block', marginTop: 12 }}>
              <span
                style={{
                  display: 'block',
                  fontSize: 13,
                  fontWeight: 600,
                  marginBottom: 6,
                  color: 'var(--text-1)',
                }}
              >
                Reason for cancelling
              </span>
              <textarea
                data-testid="leave-detail-cancel-reason"
                value={cancelReason}
                onChange={(e) => setCancelReason(e.target.value)}
                rows={3}
                placeholder="e.g. Plans changed, no longer travelling"
                autoFocus
                style={{
                  width: '100%',
                  resize: 'vertical',
                  padding: '8px 10px',
                  fontSize: 14,
                  borderRadius: 8,
                  border: '1px solid var(--border)',
                  fontFamily: 'inherit',
                }}
              />
            </label>
          </>
        ) : (
          <>
            Your approver will be notified that this request was withdrawn. This
            can&apos;t be undone — you&apos;ll need to submit a new request if you
            change your mind.
          </>
        )}
      </ConfirmationDialog>

      <Modal
        open={previewDoc !== null}
        onClose={() => setPreviewDoc(null)}
        title={previewDoc?.filename ?? 'Document'}
        maxWidth="min(960px, 92vw)"
      >
        {previewDoc !== null ? (
          <DocumentPreview leaveId={data.id} doc={previewDoc} />
        ) : null}
      </Modal>

      <ConfirmationDialog
        open={rejectOpen}
        onClose={closeRejectDialog}
        onConfirm={() => void confirmReject()}
        confirmDisabled={rejectReason.trim().length === 0}
        title={`Reject — ${data.employee.name}`}
        subtitle={`${leaveTypeLabel(data.type)} · ${dateRange}`}
        confirmLabel="Confirm reject"
        cancelLabel="Cancel"
        variant="danger"
        loading={busy}
        data-testid="leave-detail-reject"
      >
        <label style={{ display: 'block' }}>
          <span
            style={{
              display: 'block',
              fontSize: 13,
              fontWeight: 600,
              marginBottom: 6,
              color: 'var(--text-1)',
            }}
          >
            Reason (visible to employee)
          </span>
          <textarea
            id="leave-detail-reject-reason"
            data-testid="leave-detail-reject-reason"
            rows={4}
            value={rejectReason}
            onChange={(e) => setRejectReason(e.target.value)}
            autoFocus
            placeholder="e.g. Coverage is too thin that week, please pick alternative dates"
            style={{
              width: '100%',
              resize: 'vertical',
              padding: '8px 10px',
              fontSize: 14,
              borderRadius: 8,
              border: '1px solid var(--border)',
              fontFamily: 'inherit',
            }}
          />
        </label>
        {rejectError ? (
          <div
            role="alert"
            style={{
              marginTop: 10,
              padding: '8px 10px',
              background: 'var(--danger-bg)',
              border: '1px solid var(--danger-line)',
              borderRadius: 8,
              color: 'var(--danger-fg)',
              fontSize: 12,
            }}
          >
            {rejectError}
          </div>
        ) : null}
      </ConfirmationDialog>

      <Modal
        open={capacityConfirmOpen}
        onClose={() => setCapacityConfirmOpen(false)}
        title="Approve above team capacity?"
      >
        <div
          data-testid="leave-detail-capacity-confirm-modal"
          style={{ display: 'flex', flexDirection: 'column', gap: 12 }}
        >
          <p style={{ margin: 0, fontSize: 13, color: 'var(--text-1)' }}>
            Approving <strong>{data.employee.name}</strong>&rsquo;s leave puts the
            team over its coverage cap for these dates. You can still approve — it
            will be recorded as an <strong>above-capacity approval</strong> and
            flagged for HR.
          </p>
          {capacitySnapshot ? (
            <div
              data-testid="leave-detail-capacity-confirm-snapshot"
              style={{
                padding: '10px 12px',
                background: 'var(--surface-2)',
                border: '1px solid var(--border)',
                borderRadius: 8,
                fontSize: 13,
                color: 'var(--text-1)',
              }}
            >
              <strong>
                {capacitySnapshot.peakOthers + 1} of {capacitySnapshot.teamCap}
              </strong>{' '}
              · {capacitySnapshot.capPhrase}
            </div>
          ) : null}
          <div>
            <label
              htmlFor="leave-detail-capacity-note"
              style={{
                display: 'block',
                fontSize: 12,
                fontWeight: 600,
                color: 'var(--text-2)',
                marginBottom: 6,
              }}
            >
              Reason for approving above capacity{' '}
              <span style={{ color: 'var(--danger-fg)' }}>*</span>
            </label>
            <textarea
              id="leave-detail-capacity-note"
              data-testid="leave-detail-capacity-note"
              value={approvalNote}
              onChange={(e) => setApprovalNote(e.target.value)}
              maxLength={APPROVAL_NOTE_MAX}
              rows={2}
              disabled={busy}
              placeholder="Required. Why are you approving above capacity? Recorded on the activity log."
              style={{
                width: '100%',
                boxSizing: 'border-box',
                padding: '8px 10px',
                border: '1px solid var(--border)',
                borderRadius: 6,
                fontSize: 13,
                fontFamily: 'inherit',
                resize: 'vertical',
                color: 'var(--text-1)',
                background: 'var(--surface-1, white)',
              }}
            />
          </div>
          {capacityConfirmError ? (
            <div
              role="alert"
              style={{
                padding: '8px 10px',
                background: 'var(--danger-bg)',
                border: '1px solid var(--danger-line)',
                borderRadius: 8,
                color: 'var(--danger-fg)',
                fontSize: 12,
              }}
            >
              {capacityConfirmError}
            </div>
          ) : null}
          <div style={{ display: 'flex', gap: 8, justifyContent: 'flex-end' }}>
            <button
              type="button"
              onClick={() => setCapacityConfirmOpen(false)}
              style={{
                padding: '8px 14px',
                borderRadius: 8,
                border: '1px solid var(--border)',
                background: 'var(--surface)',
                color: 'var(--text-2)',
                fontSize: 13,
                fontWeight: 600,
                cursor: 'pointer',
                minHeight: 44,
              }}
            >
              Cancel
            </button>
            <button
              type="button"
              data-testid="leave-detail-capacity-confirm"
              disabled={busy || approvalNote.trim().length === 0}
              onClick={confirmApproveOverCapacity}
              style={{
                padding: '8px 14px',
                borderRadius: 8,
                border: 'none',
                background: 'var(--success)',
                color: 'white',
                fontSize: 13,
                fontWeight: 600,
                cursor:
                  busy || approvalNote.trim().length === 0
                    ? 'not-allowed'
                    : 'pointer',
                opacity: busy || approvalNote.trim().length === 0 ? 0.6 : 1,
                minHeight: 44,
              }}
            >
              {busy ? 'Approving…' : 'Approve above capacity'}
            </button>
          </div>
        </div>
      </Modal>

      <Modal
        open={directorDecisionMode !== null}
        onClose={() =>
          directorDecisionPending ? undefined : setDirectorDecisionMode(null)
        }
        title={
          directorDecisionMode === 'approve'
            ? composite
              ? 'Approve composite'
              : 'Approve overflow leave'
            : composite
              ? 'Reject composite'
              : 'Reject overflow leave'
        }
      >
        <div style={{ display: 'flex', flexDirection: 'column', gap: 12 }}>
          {composite ? (
            <p style={{ margin: 0, fontSize: 13, color: 'var(--text-2)', lineHeight: 1.5 }}>
              Decision applies to <strong>both linked legs</strong>:{' '}
              {composite.this_type_label} ({formatCompositeDays(composite.this_days)} days) +{' '}
              {composite.sibling_type_label} ({formatCompositeDays(composite.sibling_days)} days).
            </p>
          ) : (
            <p style={{ margin: 0, fontSize: 13, color: 'var(--text-2)', lineHeight: 1.5 }}>
              {directorDecisionMode === 'approve'
                ? 'Approving this overflow leave will mark it approved.'
                : 'Rejecting this overflow leave will mark it rejected.'}
            </p>
          )}
          {data.manager_justification ? (
            <blockquote
              style={{
                margin: 0,
                padding: '8px 12px',
                borderLeft: '3px solid var(--steel-blue)',
                background: 'var(--surface-2)',
                borderRadius: 4,
                fontSize: 12,
                color: 'var(--text-2)',
                lineHeight: 1.5,
              }}
            >
              <strong style={{ display: 'block', marginBottom: 4 }}>
                Approver's note:
              </strong>
              {data.manager_justification}
            </blockquote>
          ) : null}
          <label
            htmlFor="leave-detail-director-decision-note"
            style={{ fontSize: 12, color: 'var(--text-2)', fontWeight: 600 }}
          >
            Decision note (recorded on the audit trail)
          </label>
          <textarea
            id="leave-detail-director-decision-note"
            data-testid="leave-detail-director-decision-note"
            rows={5}
            value={directorDecisionNote}
            maxLength={1000}
            onChange={(e) => setDirectorDecisionNote(e.target.value)}
            placeholder={
              directorDecisionMode === 'approve'
                ? "e.g. Manager's case is solid, family commitment is genuine. Approving in full."
                : 'e.g. Coverage is too tight on those dates. Suggest the employee re-file with adjusted dates.'
            }
            style={{
              padding: 10,
              border: '1px solid var(--border)',
              borderRadius: 8,
              background: 'var(--surface)',
              fontFamily: 'inherit',
              resize: 'vertical',
            }}
          />
          <div style={{ fontSize: 11, color: 'var(--text-3)' }}>
            {directorDecisionNote.length} / 10+ chars. Visible to the employee
            and the manager.
          </div>
          {directorDecisionError ? (
            <div
              role="alert"
              style={{
                padding: '8px 10px',
                background: 'var(--danger-bg)',
                border: '1px solid var(--danger-line)',
                borderRadius: 8,
                color: 'var(--danger-fg)',
                fontSize: 12,
              }}
            >
              {directorDecisionError}
            </div>
          ) : null}
          <div style={{ display: 'flex', gap: 8, justifyContent: 'flex-end' }}>
            <button
              type="button"
              onClick={() => setDirectorDecisionMode(null)}
              disabled={directorDecisionPending}
              style={{
                padding: '8px 14px',
                borderRadius: 8,
                border: '1px solid var(--border)',
                background: 'var(--surface)',
                color: 'var(--text-2)',
                fontWeight: 600,
                cursor: 'pointer',
              }}
            >
              Cancel
            </button>
            <button
              type="button"
              data-testid="leave-detail-director-decision-submit"
              disabled={
                directorDecisionNote.trim().length < 10 ||
                directorDecisionPending
              }
              onClick={() => void handleDirectorDecisionSubmit()}
              style={{
                padding: '8px 14px',
                borderRadius: 8,
                border: 'none',
                background:
                  directorDecisionNote.trim().length < 10
                    ? 'var(--border-strong)'
                    : directorDecisionMode === 'reject'
                      ? 'var(--danger)'
                      : 'var(--success)',
                color: 'var(--surface)',
                fontWeight: 600,
                cursor:
                  directorDecisionNote.trim().length < 10 ||
                  directorDecisionPending
                    ? 'not-allowed'
                    : 'pointer',
              }}
            >
              {directorDecisionPending
                ? 'Saving…'
                : directorDecisionMode === 'approve'
                  ? composite
                    ? 'Approve composite'
                    : 'Approve overflow leave'
                  : composite
                    ? 'Reject composite'
                    : 'Reject overflow leave'}
            </button>
          </div>
        </div>
      </Modal>
    </>
  )
}

interface ModalShellProps {
  onClose: () => void
  /** data-testid for the card element (the OK state carries
   *  ``leave-detail-page``; loading / error omit it). */
  cardTestId?: string
  children: ReactNode
}

/**
 * Hand-rolled modal shell for the routable leave-detail card.
 *
 * The shared ``Modal`` primitive bakes in its own title/close header and wraps
 * everything in a single scrollable body, which can't express the fixed
 * header + scrollable body + fixed footer this card needs (or an ``<h1>``
 * title). So we hand-roll the backdrop here: fixed overlay, centred card,
 * subtle fade/scale-in that respects reduced motion, and Escape / backdrop
 * click to close. Portalled to ``document.body`` at ``zIndex: 90`` so the
 * nested ``Modal`` / ``ConfirmationDialog`` dialogs (``zIndex: 100``) always
 * stack above it.
 */
function ModalShell({ onClose, cardTestId, children }: ModalShellProps): JSX.Element {
  const reducedMotion = useReducedMotion()
  const [entered, setEntered] = useState(false)
  const onCloseRef = useRef(onClose)
  useEffect(() => {
    onCloseRef.current = onClose
  }, [onClose])
  useEffect(() => {
    const raf = requestAnimationFrame(() => setEntered(true))
    const handleKey = (event: KeyboardEvent): void => {
      if (event.key === 'Escape') {
        event.preventDefault()
        onCloseRef.current()
      }
    }
    document.addEventListener('keydown', handleKey)
    return () => {
      cancelAnimationFrame(raf)
      document.removeEventListener('keydown', handleKey)
    }
  }, [])

  const transition = reducedMotion
    ? 'none'
    : 'opacity var(--motion-duration-base) var(--motion-ease-out),' +
      ' transform var(--motion-duration-base) var(--motion-ease-out)'

  return createPortal(
    <div
      role="presentation"
      onClick={onClose}
      style={{
        position: 'fixed',
        inset: 0,
        background: 'rgba(0,0,0,0.45)',
        display: 'flex',
        alignItems: 'flex-start',
        justifyContent: 'center',
        overflowY: 'auto',
        padding: 24,
        zIndex: 90,
        opacity: entered ? 1 : 0,
        transition,
      }}
    >
      <div
        role="dialog"
        aria-modal="true"
        data-testid={cardTestId}
        onClick={(event) => event.stopPropagation()}
        style={{
          background: 'var(--surface)',
          borderRadius: 16,
          maxWidth: 720,
          width: '100%',
          maxHeight: '92vh',
          margin: 'auto',
          display: 'flex',
          flexDirection: 'column',
          overflow: 'hidden',
          boxShadow: 'var(--shadow-modal)',
          opacity: entered ? 1 : 0,
          transform: entered ? 'scale(1)' : 'scale(0.98)',
          transition,
        }}
      >
        {children}
      </div>
    </div>,
    document.body,
  )
}

/** A labelled metadata column in the detail card's metadata row. */
function MetaColumn({
  label,
  children,
}: {
  label: string
  children: ReactNode
}): JSX.Element {
  return (
    <div style={{ display: 'flex', flexDirection: 'column', gap: 4, minWidth: 0 }}>
      <span
        style={{
          fontSize: 11,
          fontWeight: 700,
          letterSpacing: '0.06em',
          textTransform: 'uppercase',
          color: 'var(--text-2)',
        }}
      >
        {label}
      </span>
      <span style={{ fontSize: 14, fontWeight: 500, color: 'var(--text)' }}>{children}</span>
    </div>
  )
}

interface TimelineRowProps {
  entry: LeaveDetailTimelineEntry
  employeeName: string
  leaveType: string
  days: number
  /** Last entry in the list — suppresses the trailing connector line. */
  isLast: boolean
}

const DECISION_VERBS: Record<string, string> = {
  'leave.approved': 'approved',
  'leave.rejected': 'rejected',
}

/** Timeline dot colour by event kind — approvals read green, rejections red,
 *  escalations amber, everything else a muted grey. */
const TIMELINE_DOT_COLOR: Record<string, string> = {
  'leave.approved': 'var(--success)',
  'leave.out_of_system_encoded': 'var(--success)',
  'leave.cancellation_approved': 'var(--success)',
  'leave.rejected': 'var(--danger)',
  'leave.cancellation_rejected': 'var(--danger)',
  'hr_review.rejected': 'var(--danger)',
  'leave.escalated': 'var(--warning)',
  'hr_review.document_requested': 'var(--warning)',
}

function TimelineRow({ entry, employeeName, leaveType, days, isLast }: TimelineRowProps): JSX.Element {
  const label = ACTION_LABEL[entry.action] ?? entry.action
  const dotColor = TIMELINE_DOT_COLOR[entry.action] ?? 'var(--text-3)'
  const when = formatTimestamp(entry.created_at)
  const verb = DECISION_VERBS[entry.action]
  const sentence =
    verb !== undefined && entry.actor_name !== null && entry.actor_role !== null
      ? buildDecisionSentence({
          actorName: entry.actor_name,
          actorRole: entry.actor_role,
          employeeName,
          leaveType,
          days,
          verb,
        })
      : null
  return (
    <li style={{ display: 'flex', gap: 12 }}>
      {/* Rail gutter — a ring dot with a connector line running down to the
          next entry (suppressed on the last row). */}
      <div
        aria-hidden
        style={{
          display: 'flex',
          flexDirection: 'column',
          alignItems: 'center',
          flexShrink: 0,
          width: 14,
        }}
      >
        <span
          style={{
            width: 12,
            height: 12,
            borderRadius: '50%',
            border: `2px solid ${dotColor}`,
            background: 'var(--surface)',
            boxSizing: 'border-box',
            marginTop: 3,
            flexShrink: 0,
          }}
        />
        {!isLast ? (
          <span
            style={{
              width: 2,
              flex: 1,
              marginTop: 4,
              borderRadius: 1,
              background: 'var(--border)',
            }}
          />
        ) : null}
      </div>
      <div style={{ flex: 1, minWidth: 0, paddingBottom: isLast ? 0 : 18 }}>
        <div style={{ fontSize: 13, fontWeight: 600, color: 'var(--text)' }}>
          {label}
          {entry.actor_name ? <span> · {entry.actor_name}</span> : null}
        </div>
        {sentence !== null ? (
          <p
            data-testid={`timeline-decision-sentence-${entry.id}`}
            style={{
              margin: '4px 0 0',
              fontSize: 13,
              color: 'var(--text-2)',
            }}
          >
            {sentence}
          </p>
        ) : null}
        {entry.note ? (
          <p
            style={{
              margin: '4px 0 0',
              padding: '6px 10px',
              borderLeft: '2px solid var(--border)',
              fontSize: 13,
              color: 'var(--text-2)',
            }}
          >
            {entry.note}
          </p>
        ) : null}
        <div style={{ fontSize: 11, color: 'var(--text-3)', marginTop: 2 }}>
          {when} · via {entry.source}
        </div>
      </div>
    </li>
  )
}

interface ActivityTimelineProps {
  timeline: ReadonlyArray<LeaveDetailTimelineEntry>
  employeeName: string
  leaveType: string
  days: number
}

/** The audit timeline list (or its empty state). Shared by the owner's
 *  "Activity" tab and the approver review, which shows it on its own now that
 *  the analysis panel is gone from the approval surface. */
function ActivityTimeline({
  timeline,
  employeeName,
  leaveType,
  days,
}: ActivityTimelineProps): JSX.Element {
  if (timeline.length === 0) {
    return (
      <p style={{ margin: 0, fontSize: 13, color: 'var(--text-3)' }}>
        Nothing to show yet.
      </p>
    )
  }
  return (
    <ol
      data-testid="leave-detail-timeline"
      style={{
        margin: 0,
        padding: 0,
        listStyle: 'none',
        display: 'flex',
        flexDirection: 'column',
      }}
    >
      {timeline.map((entry, index) => (
        <TimelineRow
          key={entry.id}
          entry={entry}
          employeeName={employeeName}
          leaveType={leaveType}
          days={days}
          isLast={index === timeline.length - 1}
        />
      ))}
    </ol>
  )
}

interface DocumentPreviewProps {
  leaveId: string
  doc: DocRef
}

/**
 * Inline preview body rendered inside the modal. The api streams the
 * bytes back through cookie-auth (see ``getDocumentUrl``) so the
 * <iframe>/<img> just points at our own host — the S3 URL never reaches
 * the browser.
 *
 * Images get an <img> with object-fit: contain so the modal sizing
 * controls the layout. PDFs and anything else fall back to <iframe>,
 * which lets the browser's built-in viewer handle scrolling + zoom.
 */
function DocumentPreview({ leaveId, doc }: DocumentPreviewProps): JSX.Element {
  const url = getDocumentUrl(leaveId, doc.id)
  const isImage = doc.mimeType.startsWith('image/')
  // Cap the viewer height so the modal stays comfortably under viewport
  // (panel max-height is 90vh, header + padding eat ~120px).
  const bodyHeight = 'min(70vh, 720px)'

  return (
    <div
      data-testid={`doc-preview-${doc.id}`}
      style={{ display: 'flex', flexDirection: 'column', gap: 10 }}
    >
      <div
        style={{
          width: '100%',
          height: bodyHeight,
          background: 'var(--surface-2)',
          border: '1px solid var(--border)',
          borderRadius: 8,
          overflow: 'hidden',
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
        }}
      >
        {isImage ? (
          <img
            src={url}
            alt={doc.filename}
            style={{
              maxWidth: '100%',
              maxHeight: '100%',
              objectFit: 'contain',
              display: 'block',
            }}
          />
        ) : (
          <iframe
            src={url}
            title={doc.filename}
            style={{ width: '100%', height: '100%', border: 'none' }}
          />
        )}
      </div>
      <div
        style={{
          display: 'flex',
          alignItems: 'center',
          gap: 8,
          fontSize: 12,
          color: 'var(--text-3)',
        }}
      >
        <span>{doc.mimeType}</span>
        <span aria-hidden="true">·</span>
        <span>{formatBytes(doc.byteSize)}</span>
        <span style={{ flex: 1 }} />
        <a
          href={url}
          target="_blank"
          rel="noopener noreferrer"
          style={{
            display: 'inline-flex',
            alignItems: 'center',
            gap: 4,
            color: 'var(--steel-blue)',
            textDecoration: 'none',
            fontWeight: 600,
          }}
        >
          <Icon name="open_in_new" size={14} aria-hidden />
          Open in new tab
        </a>
      </div>
    </div>
  )
}


/**
 * Re-runs the rule engine against a persisted leave and renders the
 * familiar bucket-grouped Impact preview — the same view the requester
 * saw on /apply. Warnings aren't stored on the leave row so this is a
 * live read against current team state.
 */
interface ImpactPreviewSectionProps {
  leaveId: string
  /** Full leave-detail payload — used for status gating (staleTime) and
   *  the HR-only Context section. */
  leave?: LeaveDetailPayload | null
  /** Defaults to ``'employee'`` — controls HR-context visibility. */
  viewerRole?:
    | 'employee'
    | 'manager'
    | 'hod'
    | 'director'
    | 'exec_director'
    | 'hr'
  /** True when the viewer is the leave's owner — rephrases the pattern-trigger
   *  copy from the approver's "approving this…" to the owner's "this leave…". */
  viewerIsOwner?: boolean
}

/**
 * Wraps LeaveImpactCard with the leave-detail query. The card today
 * does double duty for both the Apply page and the approver review;
 * when ``viewerRole`` is set to an approver tier, the card switches
 * into Phase 3 mode (action-line headline, citation chips, Balance +
 * Approver-routing buckets).
 */
function ImpactPreviewSection({
  leaveId,
  leave = null,
  viewerRole = 'employee',
  viewerIsOwner = false,
}: ImpactPreviewSectionProps): JSX.Element | null {
  // Regenerate on every refresh while the leave is in-flight (not yet
  // approved or cancelled). Once a terminal status is reached, the
  // analysis is locked — use a long staleTime so the cached content
  // survives React Query's GC window.
  const leaveStatus = leave?.status?.toLowerCase() ?? 'pending'
  const isTerminal = leaveStatus === 'approved' || leaveStatus === 'cancelled'
  const q = useQuery({
    queryKey: ['leaves', 'preview-existing', leaveId],
    queryFn: () => previewExistingLeave(leaveId),
    staleTime: isTerminal ? 5 * 60_000 : 0,
    refetchOnMount: isTerminal ? true : 'always',
    retry: false,
  })

  const rule = q.data ?? null

  // Deterministic team-calendar facts. These keep the Coverage / Blackout
  // rows truthful even when the rule engine raised no warning — the card
  // must never read "No coverage concern" while peers are demonstrably out.
  const facts = leave !== null ? deriveImpactFacts(leave) : null

  // "At a glance" facts checklist — the same deterministic panel the apply
  // page shows before submit (LeaveCheck), re-stated for a filed leave. This
  // replaced the dark ILMU assistant panel: facts-only, no verdict. Coverage
  // + blackout come from `facts` (available immediately); duration + pattern
  // rows fill in once the live rule preview (`rule`) returns.
  const checkModel =
    leave !== null && facts !== null
      ? buildLeaveCheckItems(leave, rule, facts, viewerIsOwner)
      : null

  const factsPanel = checkModel ? (
    <LeaveCheck
      hideHeader
      bare
      ready
      items={checkModel.items}
      persistentItems={checkModel.persistentItems}
      footnote="△ = worth a look, not a blocker."
    />
  ) : null

  return (
    <div style={{ display: 'flex', flexDirection: 'column', gap: 12 }}>
      {factsPanel}
    </div>
  )
}

/**
 * Derive the deterministic facts the ILMU panel states — peers actually out
 * during the requested window, and any blackout overlapping the dates —
 * straight from the leave-detail ``team_calendar``. This is the single
 * source of truth the rule-engine warnings are reconciled against; the
 * calendar grid and the panel rows can no longer disagree.
 */
function deriveImpactFacts(leave: LeaveDetailPayload): LeaveDetailFacts {
  const tc = leave.team_calendar
  // Restrict to the requested leave days (the calendar pads ±2 days).
  const seen = new Map<string, string>()
  for (const day of tc.days) {
    if (!day.is_in_leave_window) continue
    for (const m of day.out) {
      if (m.is_requester) continue
      // Coverage counts APPROVED absences only — a pending teammate bar is
      // tentative (shown dashed on the calendar) and does not reserve a slot,
      // matching the capacity gate and the rule engine's approved-only count.
      // Legacy rows may omit status; treat a missing status as approved so we
      // don't silently drop confirmed absences.
      if (m.status != null && m.status !== 'approved') continue
      if (!seen.has(m.employee_id)) {
        seen.set(
          m.employee_id,
          displayName({ name: m.name, preferredName: m.preferred_name }),
        )
      }
    }
  }
  const names = Array.from(seen.values())
  const overlapping = tc.blackouts.find(
    (b) => b.start_date <= leave.end_date && b.end_date >= leave.start_date,
  )
  return {
    teammatesOut: { count: names.length, names },
    overlappingBlackout: overlapping ? overlapping.name : null,
  }
}

/** Narrow the page-level viewerRole string to the LeaveImpactCard's
 *  enum. Unknown roles fall back to 'employee' which renders the
 *  Apply-style card. */
function normaliseImpactViewerRole(
  role: string,
):
  | 'employee'
  | 'manager'
  | 'hod'
  | 'director'
  | 'exec_director'
  | 'hr' {
  if (
    role === 'manager' ||
    role === 'hod' ||
    role === 'director' ||
    role === 'exec_director' ||
    role === 'hr'
  ) {
    return role
  }
  return 'employee'
}

/** Lightweight balance-state derivation for the LeaveImpactCard
 *  Balance bucket. Pulled from the rule's warnings so the card stays
 *  purely presentational. */
function deriveBalanceStateFromRule(
  rule: { warnings: ReadonlyArray<{ code: string }> } | null,
):
  | 'ample'
  | 'adequate'
  | 'narrow'
  | 'exhausted'
  | 'overdrawn'
  | null {
  if (rule === null) return null
  if (rule.warnings.some((w) => w.code === 'insufficient_balance')) return 'overdrawn'
  if (rule.warnings.some((w) => w.code === 'balance_exceeded')) return 'exhausted'
  // We don't have a fuller balance read here; the card omits the
  // bucket when this returns null. Phase 4 will plumb the BalanceState
  // enum directly off the leave detail payload.
  return null
}

/** Build an ApproverRouting struct from next_reviewer + escalation
 *  hints in the rule result. Returns null when there's nothing
 *  escalation-worthy to surface. */
function deriveApproverRoutingFromRule(
  rule: {
    suggestedStatus: 'pending' | 'escalated'
    warnings: ReadonlyArray<{ code: string }>
  } | null,
  nextReviewerName: string | null,
  _nextReviewerRole: string | null,
): {
  primary: string
  escalations: ReadonlyArray<{ approver: string; reason: string }>
} | null {
  if (rule === null || nextReviewerName === null) return null
  if (rule.suggestedStatus !== 'escalated') return null
  const hasUnpaid = rule.warnings.some((w) => w.code === 'unpaid_escalation')
  const hasBlackout = rule.warnings.some((w) => w.code === 'blackout_overlap')
  if (hasUnpaid) {
    return {
      primary: nextReviewerName,
      escalations: [
        {
          approver: 'Director',
          reason: 'Unpaid Leave requires Director approval',
        },
      ],
    }
  }
  if (hasBlackout) {
    return {
      primary: nextReviewerName,
      escalations: [
        {
          approver: 'Director',
          reason: 'Dates overlap an active blackout window',
        },
      ],
    }
  }
  return {
    primary: nextReviewerName,
    escalations: [
      {
        approver: 'Director',
        reason: 'Request crossed an escalation threshold',
      },
    ],
  }
}

function formatBytes(bytes: number): string {
  if (bytes < 1024) return `${bytes} B`
  if (bytes < 1024 * 1024) return `${Math.round(bytes / 1024)} KB`
  return `${(bytes / (1024 * 1024)).toFixed(1)} MB`
}

// ── helpers ───────────────────────────────────────────────────────────────────

/** Muted sub-line under a metadata-grid value. */
const META_SUBLINE_STYLE: CSSProperties = {
  display: 'block',
  fontWeight: 400,
  fontSize: 11,
  color: 'var(--text-3)',
  marginTop: 2,
}

/** Half-day detail sub-line for the Dates cell ("AM half day", etc.), or null. */
function deriveHalfDayDetail(leave: LeaveDetailPayload): string | null {
  if (!leave.half_day && !leave.half_start && !leave.half_end) return null
  if (leave.start_date === leave.end_date) {
    const slot = leave.half_days?.[0]?.slot
    return slot ? `${slot} half day` : 'Half day'
  }
  const parts: string[] = []
  if (leave.half_start) parts.push('half day at start')
  if (leave.half_end) parts.push('half day at end')
  return parts.length > 0 ? parts.join(', ') : 'Includes a half day'
}

/** "unpaid_leave_taken" → "Unpaid leave taken" for the rhythm sub-line. */
function humanisePatternCode(code: string): string {
  const words = code.split('_').filter(Boolean)
  if (words.length === 0) return code
  const first = words[0] ?? ''
  return [first.charAt(0).toUpperCase() + first.slice(1), ...words.slice(1)].join(' ')
}

function leaveTypeLabel(t: string): string {
  // Canonical short label from the palette ("Annual", "Marriage", …) plus
  // " leave", so the title reads the same wording the leave-type picker and
  // policy doc use. Falls back to title-casing for any unknown id.
  const palette = LEAVE_TYPE_STYLE[t as LeaveTypeId]
  if (palette) return `${palette.label} leave`
  return t.replace(/_/g, ' ').replace(/\b\w/g, (c) => c.toUpperCase()) + ' leave'
}

function formatDateRange(start: string, end: string): string {
  if (start === end) return fmtDateLong(start)
  return `${fmtDateLong(start)} - ${fmtDateLong(end)}`
}

function formatTimestamp(iso: string): string {
  const d = new Date(iso)
  return d.toLocaleString('en-GB', {
    day: 'numeric',
    month: 'short',
    hour: '2-digit',
    minute: '2-digit',
  })
}

